// Shared Studio Documents client (ODS-DOCUMENTS-001 P4).
//
// Documents is a platform primitive: the bytes live in object storage behind the
// shared Studio Platform and extraction is the platform's canonical contract.
// Every call here goes through the Studio Worker proxy
// (`/organizations/{orgId}/documents/...`), which authenticates the Studio user,
// bridges them onto their platform identity and then authorizes the operation as
// that member. The browser therefore never holds an object-store credential, a
// platform service token, or a bridged platform token.
import { ApiError } from "./types";
import { queryString, raise, type GovernanceClientOptions, type QueryValue } from "./governanceHttp";

// The platform stores and filters these lowercase (`documents.status` is
// `CHECK (status IN ('active', 'deleted'))`), and its list route compares the
// `status` query value literally, so an uppercase filter silently matches nothing.
export type DocumentStatus = "active" | "deleted" | "purged" | "quarantined";

export interface StudioDocument {
  id: string;
  organizationId: string;
  ownerId: string;
  filename: string;
  displayName: string;
  mimeType: string;
  extension: string;
  size: number;
  /** Checksum of the stored bytes, as Documents recorded them. */
  checksum: string;
  latestVersion: number;
  classification: string;
  status: DocumentStatus;
  createdAt: string;
  updatedAt: string;
}

/** Why extraction produced no text. A governed outcome, not a transport error. */
export type ExtractionFailureCode = "OCR_REQUIRED" | "UNSUPPORTED_MEDIA_TYPE" | "CORRUPT_DOCUMENT";

export interface ExtractionResult {
  documentId: string;
  organizationId: string;
  versionNumber: number;
  status: "extracted" | "unsupported";
  filename: string;
  mediaType: string;
  byteSize: number;
  extractor: string;
  extractorVersion: string;
  extractedAt: string;
  text: string | null;
  characterCount: number;
  pageCount: number | null;
  documentChecksum: string;
  extractionChecksum: string | null;
  failureCode: ExtractionFailureCode | null;
  failureReason: string | null;
}

export interface UploadDocumentInput {
  filename: string;
  mimeType: string;
  /** Base64 of the exact bytes selected, so the platform hashes what the user picked. */
  contentBase64: string;
  declaredSize: number;
  idempotencyKey: string;
  displayName?: string;
  classification?: string;
}

/**
 * Text-first media types the shared extractor supports today. OCR is a separate
 * Documents Platform capability, so an image or a scanned page is refused with
 * an explicit `OCR_REQUIRED` rather than imported as empty text.
 */
export const SUPPORTED_DOCUMENT_MIME_TYPES: readonly string[] = [
  "text/plain",
  "text/markdown",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

/** Encodes selected bytes without blowing the argument limit on a large file. */
export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export class DocumentsClient {
  private baseUrl: string;
  private getToken: () => string | null;
  private onUnauthorized: (() => void) | undefined;

  constructor(opts: GovernanceClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
  }

  path(orgId: string, subpath = ""): string {
    const tail = subpath.replace(/^\/+/, "");
    return `${this.baseUrl}/organizations/${orgId}/documents${tail ? `/${tail}` : ""}`;
  }

  private async request<T>(
    method: string,
    orgId: string,
    subpath: string,
    body?: unknown,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);

    let res: Response;
    try {
      res = await fetch(this.path(orgId, subpath), init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    const data = await parse(res);
    if (!res.ok) raise(res.status, data);
    return data as T;
  }

  list(orgId: string, filter: Record<string, QueryValue> = {}) {
    return this.request<{ documents: StudioDocument[] }>(
      "GET",
      orgId,
      queryString(filter),
    );
  }

  get(orgId: string, documentId: string) {
    return this.request<{ document: StudioDocument }>("GET", orgId, documentId);
  }

  upload(orgId: string, input: UploadDocumentInput) {
    return this.request<{ document: StudioDocument }>("POST", orgId, "", input);
  }

  /** Canonical extraction. Deterministic for the same bytes and extractor. */
  extract(orgId: string, documentId: string, versionNumber?: number) {
    return this.request<ExtractionResult>(
      "POST",
      orgId,
      `${documentId}/extract`,
      versionNumber === undefined ? {} : { versionNumber },
    );
  }

  softDelete(orgId: string, documentId: string) {
    return this.request<{ document: StudioDocument }>("DELETE", orgId, documentId);
  }
}
